import { IconName } from "PFTheme/graphics/icons";
import { Id } from "PFTypes/id";

import { DateRangePair } from "./date_time";
import { Snakify } from "./snakify";
import { Source } from "./source";

export enum Experience {
  None = 0,
  Basic = 1,
  Developing = 2,
  Intermediate = 3,
  Proficient = 4,
  Advanced = 5
}

export const DEFAULT_SKILL_EXPERIENCE = Experience.Intermediate;

export enum Importance {
  Supporting = 0,
  Essential = 1,
  Required = 2
}

export type _CustomValue = {
  assessed: boolean;
  assessmentSource: string;
  certificateIds: Array<number>;
  createdAt: string;
  developmental: boolean;
  expiryDate: string | null;
  globalId: string | null;
  id: Id;
  interest: boolean | null;
  rare: boolean | null;
  seniority: number;
  source: Source | null;
  suggestedExperience: number;
  text: string;
  top: boolean;
  value: string | number;
  emVerified: boolean;
  customTypeId?: Id;
  state: CustomValueState;
  importance?: Importance;
  description: string | null;
  globalDescription: string | null;
  updatedAt: string;
  creator?: {
    id: Id;
    name: string;
  };
  skillsFrameworks?: {
    id: Id;
    name: string;
  }[];
  activitiesCount?: number;
  profilesCount?: number;
  skillsFrameworksCount?: number;
  locked?: boolean;
  lastMergedInto?: { id: Id; value: string | number } | null;
  mergedCustomValues: { id: Id; value: string | number }[];
  tagIds: Id[];
};

export type CustomValue = Snakify<_CustomValue>;

export type ProfileCustomValue = _CustomValue & {
  experience: Experience;
};

type OriginalCustomValue = Pick<
  _CustomValue,
  "id" | "state" | "text" | "value" | "globalId" | "customTypeId"
>;

export type MatchedCustomValue = {
  score: number;
  idfBoost: number;
  customValue: ProfileCustomValue;
  originalCustomValue?: OriginalCustomValue;
};

type _ActivityCustomValue = _CustomValue & {
  requiredExperience: Experience;
};

export type ActivityCustomValue = Snakify<_ActivityCustomValue>;

export type MatchCustomValue = ProfileCustomValue & {
  requiredExperience?: Experience;
};

export type PureCustomValueCamelCased = {
  id: Id;
  globalId?: string | null;
  ordinal?: number;
  added?: boolean;
  text: string | boolean;
  type?: string;
  curatedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  description?: string | null;
  importance?: Importance;
  state?: CustomValueState;
  value?: string | number | boolean;
  profilesCount?: number;
  activitiesCount?: number;
  skillsFrameworksCount?: number;
  tagIds?: Id[];
  created?: boolean;
};

export type CustomValueDateRange = Snakify<DateRangePair>;

export type PureCustomValue = Snakify<PureCustomValueCamelCased>;

export type ActivityPureCustomValue = Snakify<
  PureCustomValueCamelCased & { requiredExperience?: Experience }
>;

export type PureCustomValueData = Partial<Omit<ActivityPureCustomValue, "id" | "value">> &
  Pick<ActivityPureCustomValue, "id" | "value">;

export enum CustomValueState {
  Approved = "approved",
  Blocked = "blocked",
  Created = "created",
  Merged = "merged"
}

enum CustomValueMetaAction {
  Merge = "merge",
  Block = "block",
  Approve = "approve",
  Edit = "edit",
  Export = "export",
  Reset = "reset"
}

export type CustomValueMetaActionName = `${CustomValueMetaAction}`;

export type CustomValueActivityConnection = {
  id: Id;
  activityId: Id;
  customValueId: Id;
  customTypeId: Id;
  requiredExperience: Experience;
  importance: Importance;
  source: Source | null;
  createdAt: string;
  updatedAt: string;
};

export type CustomValueInsights = {
  experienceStats: Record<
    | "unranked"
    | "notApplicable"
    | "basic"
    | "developing"
    | "intermediate"
    | "proficient"
    | "advanced"
    | "total",
    number
  > | null;
};

export type CustomValueTag = {
  id: Id;
  name: string;
  displayAs: string;
  iconName: IconName;
  customValueCount: number;
};
